import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import Select from "react-select";

import { H5, P } from "../../atoms/Texts";
import { DarkFormField, DarkTextArea } from "../../atoms/Inputs";
import {
  getCities,
  getNewToken,
  getStates,
} from "../../../shared/helpers/util.country";
import languages from "../../../shared/fixtures/languages";
import telephone_codes from "../../../shared/fixtures/telephone_codes";
import phone_types from "../../../shared/fixtures/phone_types";
import { NewProgramFooter } from "../../organisms/Footers/footer.new.program";
import { selectStyle } from "../../../shared/fixtures/select_style";

const EditProgramFormThree = ({ discard, fields, updateFields, saveToDraft }) => {
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors: formStateErrors },
  } = useForm();

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);

  const changeState = async (state) => {
    const cities = await getCities({ state: state });
    setCities(cities);
  };

  const changeCountry = async (country) => {
    const states = await getStates({ country: country });
    setStates(states);
  };

  const getCountryToken = async () => {
    const countries = await getNewToken();
    setCountries(countries);
  };

  useEffect(() => {
    getCountryToken();
  }, []);

  const arrangeLanguages = (languages) => {
    let _languages = [];
    if (languages) {
      languages.split(",").forEach((language) => {
        _languages.push({
          label: language,
          value: language,
        });
      });
    }
    return _languages;
  };

  const findExtension = (extension) => {
    return telephone_codes.find((code) => code.value === extension)?.label;
  };

  const defaultVals = {
    "location-name": fields?.location?.name || "",
    "location-address": fields.location?.address || "",
    "location-region": fields.location?.region || "",
    "location-zipCode": fields.location?.zipCode || "",
    "location-transportation": fields.location?.transportation || "",
    "location-accessibiltyForDisabilities":
      fields?.location?.accessibiltyForDisabilities || "",

    "location-country":
      {
        label: fields?.location?.country,
        value: fields?.location?.country,
      } || "",

    "location-stateOrProvince":
      {
        label: fields.location?.stateOrProvince,
        value: fields.location?.stateOrProvince,
      } || "",

    "location-city":
      {
        label: fields.location?.city,
        value: fields.location?.city,
      } || "",
  };

  useEffect(() => {
    if (fields) {
      reset(defaultVals);
    }
  }, [fields]);

  const updateData = (data) => {
    updateFields(data);
  };

  return (
    <main className="">
      <form onSubmit={handleSubmit(updateData)}>
        <section className="pb-20 w-8/12 organization-container">
          {/* Location */}
          <H5 className="mb-4">Location</H5>
          <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <div key={`location-name`} className="col-span-2">
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`location-name`}
                  name={`location-name`}
                  placeholder="..."
                  label="Name"
                  containerClass="flex-grow"
                  defaultValue={fields?.location?.name}
                  {...register("location-name")}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-name`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div key={`location-address`} className=" col-span-2">
              <div className="flex items-center justify-between">
                <DarkTextArea
                  rows={3}
                  id={`location-address`}
                  name={`location-address`}
                  placeholder="..."
                  label="Address"
                  defaultValue={fields?.location?.address}
                  containerClass="flex-grow"
                  {...register("location-address", {})}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-address`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div className="mb-4">
              {fields?.location?.country && (
                <>
                  <label htmlFor={`location-country`}>
                    <P
                      fontSize="14px"
                      fontFamily="semi"
                      color="#47495C"
                      className="mb-2"
                    >
                      Country
                      <span
                        className=""
                        style={{
                          color: "#E62250",
                        }}
                      >
                        *
                      </span>
                    </P>
                  </label>
                  <Controller
                    control={control}
                    id={`location-country`}
                    name={`location-country`}
                    render={({ field }) => (
                      <Select
                        {...field}
                        styles={selectStyle}
                        autoComplete="true"
                        isLoading={countries?.length < 1}
                        isDisabled={countries?.length < 1}
                        options={countries}
                        onChange={(option) => {
                          setValue("location-stateOrProvince", "");
                          setValue("location-city", "");
                          changeCountry(option.value);
                          field.onChange(option);
                        }}
                        placeholder="--Select a country--"
                        className="w-full"
                      />
                    )}
                  />
                  {formStateErrors[`location-country`] && (
                    <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                      Select a country
                    </P>
                  )}
                </>
              )}
            </div>

            <div key={`location-region`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="text"
                  id={`location-region`}
                  name={`location-region`}
                  placeholder="..."
                  label="Region"
                  containerClass="flex-grow"
                  defaultValue={fields?.location?.region}
                  {...register("location-region")}
                />
              </div>
            </div>

            <div className="mb-4">
              {fields?.location?.stateOrProvince && (
                <>
                  <label htmlFor={`location-stateOrProvince`}>
                    <P
                      fontSize="14px"
                      fontFamily="semi"
                      color="#47495C"
                      className="mb-2"
                    >
                      State or Province
                      <span
                        className=""
                        style={{
                          color: "#E62250",
                        }}
                      >
                        *
                      </span>
                    </P>
                  </label>
                  <Controller
                    control={control}
                    id={`location-stateOrProvince`}
                    name={`location-stateOrProvince`}
                    render={({ field }) => (
                      <Select
                        {...field}
                        styles={selectStyle}
                        autoComplete="true"
                        isLoading={states?.length < 1}
                        isDisabled={states?.length < 1}
                        options={states}
                        onChange={(option) => {
                          setValue("location-city", "");
                          changeState(option.value);
                          field.onChange(option);
                        }}
                        placeholder="--Select a state--"
                        className="w-full"
                      />
                    )}
                  />
                  {formStateErrors[`location-stateOrProvince`] && (
                    <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                      Select a state
                    </P>
                  )}
                </>
              )}
            </div>

            <div className="mb-4">
              {fields?.location?.city && (
                <>
                  <label htmlFor={`location-city`}>
                    <P
                      fontSize="14px"
                      fontFamily="semi"
                      color="#47495C"
                      className="mb-2"
                    >
                      City
                    </P>
                  </label>
                  <Controller
                    control={control}
                    id={`location-city`}
                    name={`location-city`}
                    render={({ field }) => (
                      <Select
                        styles={selectStyle}
                        {...field}
                        autoComplete="true"
                        isLoading={cities?.length < 1}
                        isDisabled={cities?.length < 1}
                        placeholder="--Select a city--"
                        className="w-full"
                      />
                    )}
                  />
                </>
              )}
            </div>

            <div key={`location-zipCode`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="number"
                  id={`location-zipCode`}
                  name={`location-zipCode`}
                  placeholder="..."
                  label="Zip Code"
                  defaultValue={fields?.location?.zipCode}
                  containerClass="flex-grow"
                  {...register("location-zipCode", {})}
                />
              </div>

              <ErrorMessage
                errors={formStateErrors}
                name={`location-zipCode`}
                as={
                  <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm" />
                }
              >
                {({ messages }) =>
                  messages &&
                  Object.entries(messages).map(([type, message]) => (
                    <P key={type}>{message}</P>
                  ))
                }
              </ErrorMessage>
            </div>

            <div className="mb-4">
              {fields?.location?.languages && (
                <>
                  <label htmlFor={`location-languages`}>
                    <P
                      fontSize="14px"
                      fontFamily="semi"
                      color="#47495C"
                      className="mb-2"
                    >
                      Languages
                      <span
                        className=""
                        style={{
                          color: "#E62250",
                        }}
                      >
                        *
                      </span>
                    </P>
                  </label>
                  <Controller
                    control={control}
                    name={`location-languages`}
                    render={({ field }) => (
                      <Select
                        {...field}
                        styles={selectStyle}
                        isMulti
                        options={languages}
                      />
                    )}
                    rules={{
                      validate: (value) => Array.isArray(value) && value.length > 0,
                    }}
                    defaultValue={
                      arrangeLanguages(fields?.location?.languages) || []
                    }
                  />

                  {formStateErrors[`location-languages`] && (
                    <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                      Select at least one language
                    </P>
                  )}
                </>
              )}
            </div>

            <div key={`location-transportation`} className=" col-span-2">
              <div className="flex items-center justify-between">
                <DarkTextArea
                  rows={3}
                  id={`location-transportation`}
                  name={`location-transportation`}
                  placeholder="..."
                  label="Transportation"
                  containerClass="flex-grow"
                  // defaultValue={fields?.location?.transportation}
                  {...register("location-transportation")}
                />
              </div>
            </div>

            <div
              key={`location-accessibiltyForDisabilities`}
              className=" col-span-2"
            >
              <div className="flex items-center justify-between">
                <DarkTextArea
                  rows={3}
                  id={`location-accessibiltyForDisabilities`}
                  name={`location-accessibiltyForDisabilities`}
                  placeholder="..."
                  label="Accessibility for Disabilities"
                  // defaultValue={fields?.location?.accessibiltyForDisabilities}
                  containerClass="flex-grow"
                  {...register("location-accessibiltyForDisabilities")}
                />
              </div>
            </div>
          </div>

          {/* Phone */}
          <H5 className="mb-4 mt-16">Phone</H5>
          <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-x-5">
            <div key={`phone-number`}>
              <div className="flex items-center justify-between">
                <DarkFormField
                  type="tel"
                  id={`phone-number`}
                  name={`phone-number`}
                  placeholder="..."
                  label="Number"
                  containerClass="flex-grow"
                  defaultValue={fields?.phones?.[0]?.number}
                  {...register("phone-number", {})}
                />
              </div>
            </div>

            <div key={`phone-extension`} className="mb-4">
              <label htmlFor="opCategory">
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Extension
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id={`phone-extension`}
                name={`phone-extension`}
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    options={telephone_codes}
                    placeholder="--Select an extension--"
                    className="w-full"
                  />
                )}
                defaultValue={
                  {
                    label: findExtension(fields.phones?.[0]?.extension),
                    value: fields.phones?.[0]?.extension,
                  } || ""
                }
              />
              {formStateErrors[`phone-extension`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select an extension
                </P>
              )}
            </div>

            <div key={`phone-type`} className="mb-4">
              <label htmlFor="opCategory">
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Phone Type
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                id={`phone-type`}
                name={`phone-type`}
                render={({ field }) => (
                  <Select
                    {...field}
                    styles={selectStyle}
                    autoComplete="true"
                    options={phone_types}
                    placeholder="--Select an type--"
                    className="w-full"
                  />
                )}
                defaultValue={
                  {
                    label: fields.phones?.[0]?.type,
                    value: fields.phones?.[0]?.type,
                  } || ""
                }
              />
              {formStateErrors[`phone-extension`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select an phone type
                </P>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor={`phone-language`}>
                <P
                  fontSize="14px"
                  fontFamily="semi"
                  color="#47495C"
                  className="mb-2"
                >
                  Language
                  <span
                    className=""
                    style={{
                      color: "#E62250",
                    }}
                  >
                    *
                  </span>
                </P>
              </label>
              <Controller
                control={control}
                name={`phone-language`}
                render={({ field }) => (
                  <Select {...field} styles={selectStyle} options={languages} />
                )}
                defaultValue={
                  {
                    label: fields.phones?.[0]?.language,
                    value: fields.phones?.[0]?.language,
                  } || ""
                }
              />

              {formStateErrors[`phone-language`] && (
                <P fontSize="12px" className="mt-2 mb-4 text-red-400 text-sm">
                  Select a language
                </P>
              )}
            </div>
          </div>
        </section>
        {/* <PrimaryButton onClick={validateForm}>Continue</PrimaryButton> */}
        <NewProgramFooter
          changeStep={updateFields}
          submitForm={updateData}
          lastStep
          discard={discard}
          saveToDraft={() => {
            const values = getValues();
            saveToDraft(values);
          }}
        />
      </form>
    </main>
  );
};

export default EditProgramFormThree;
